// The end-user Systems view (docs/systems.md "Systems view"): /systems with its headline, map, "needs attention" and the
// list of integrations; /systems/:code and /integrations/:process. Pure helpers: the state in the URL (range, sort),
// the counts of the headline, what needs attention, the queries that search a system's or an integration's data, the
// paths, and what a search names (the stage's card). Tested with node --test.
import type { Integration } from './api.ts';

// ---------------------------------------------------------------- the window

export const RANGES = ['1h', '24h', '7d'] as const;
export type SysRange = (typeof RANGES)[number];
export const DEFAULT_RANGE: SysRange = '24h';
const FROM: Record<SysRange, string> = { '1h': 'now-1h', '24h': 'now-24h', '7d': 'now-7d' };
const WORDS: Record<SysRange, string> = { '1h': 'last hour', '24h': 'last 24\u00a0h', '7d': 'last 7\u00a0days' };
export const RANGE_LABEL: Record<SysRange, string> = { '1h': '1 h', '24h': '24 h', '7d': '7 d' };

/** The range in the URL (`range=1h|24h|7d`), the default when missing or unknown. */
export function readRange(p: URLSearchParams): SysRange {
  const r = p.get('range');
  return (RANGES as readonly string[]).includes(r ?? '') ? (r as SysRange) : DEFAULT_RANGE;
}
/** The URL with a range: the default leaves it. */
export function withRange(p: URLSearchParams, r: SysRange): URLSearchParams {
  const out = new URLSearchParams(p);
  if (r === DEFAULT_RANGE) out.delete('range'); else out.set('range', r);
  return out;
}
export const rangeFrom = (r: SysRange) => FROM[r];
export const rangeWords = (r: SysRange) => WORDS[r];

// ---------------------------------------------------------------- the integrations of the window

/**
 * The integrations of seven days (where data goes, failing, overdue, cadence) with the runs, failures and documents of
 * the window: one without runs in it has none. The same rule as the cloud's systemview.ts withWindow.
 */
export function withWindow(week: readonly Integration[], ranged: readonly Integration[] | null): Integration[] {
  if (!ranged) return [...week];
  const by = new Map(ranged.map((i) => [integrationKey(i), i]));
  return week.map((i) => {
    const r = by.get(integrationKey(i));
    return { ...i, runs: r?.runs ?? 0, failed: r?.failed ?? 0, documents: r?.documents ?? 0 };
  });
}

export const integrationKey = (i: Pick<Integration, 'process' | 'from' | 'to'>) => `${i.process}\u0000${i.from ?? ''}\u0000${i.to ?? ''}`;

export interface Headline { systems: number; integrations: number; failing: number; overdue: number }

/**
 * One row per integration for the lists: a process through several systems (a `path`: Clockify → MDH → Procountor) is
 * one row however many hops it has (the map keeps a line per hop); its runs and status are the same on every hop.
 */
export function oneRowPerProcess(list: readonly Integration[]): Integration[] {
  const seen = new Set<string>();
  const out: Integration[] = [];
  for (const i of list) {
    if (i.path && i.path.length > 2) {
      if (seen.has(i.process)) continue;
      seen.add(i.process);
      out.push({ ...i, from: i.path[0] ?? i.from, to: i.path[i.path.length - 1] ?? i.to });
    } else out.push(i);
  }
  return out;
}

/** "14 systems · 17 integrations · 1 failing · 1 overdue": the systems the integrations touch, the integrations, by status. */
export function headlineOf(list: readonly Integration[]): Headline {
  const systems = new Set<string>();
  for (const i of list) { if (i.from) systems.add(i.from); if (i.to) systems.add(i.to); }
  return {
    systems: systems.size, integrations: oneRowPerProcess(list).length,
    failing: oneRowPerProcess(list).filter((i) => i.status === 'failing').length, overdue: oneRowPerProcess(list).filter((i) => i.status === 'quiet').length,
  };
}

/** What needs attention: the failing ones first (most failed runs first), then the overdue ones (the longest silent first). */
export function attentionOf(list: readonly Integration[]): Integration[] {
  const failing = list.filter((i) => i.status === 'failing').sort((a, b) => b.failed - a.failed || b.runs - a.runs || a.process.localeCompare(b.process));
  const quiet = list.filter((i) => i.status === 'quiet').sort((a, b) => (a.lastRun ?? 0) - (b.lastRun ?? 0) || a.process.localeCompare(b.process));
  return [...failing, ...quiet];
}

export const SORTS = ['attention', 'runs', 'name'] as const;
export type SysSort = (typeof SORTS)[number];
export const SORT_LABEL: Record<SysSort, string> = { attention: 'Attention', runs: 'Runs', name: 'Name' };

export function readSort(p: URLSearchParams): SysSort {
  const s = p.get('sort');
  return (SORTS as readonly string[]).includes(s ?? '') ? (s as SysSort) : 'attention';
}
export function withSort(p: URLSearchParams, s: SysSort): URLSearchParams {
  const out = new URLSearchParams(p);
  if (s === 'attention') out.delete('sort'); else out.set('sort', s);
  return out;
}

const RANK: Record<Integration['status'], number> = { failing: 0, quiet: 1, ok: 2 };
const shown = (process: string) => process.replace(/^\s*\[[^\]]{1,24}\]\s*/, '').toLowerCase();

/** The list sorted: by attention (failing, overdue, then the busiest), by runs, or by name (without the [PREFIX]). */
export function sortIntegrations(list: readonly Integration[], sort: SysSort): Integration[] {
  const byName = (a: Integration, b: Integration) => shown(a.process).localeCompare(shown(b.process)) || (a.to ?? '').localeCompare(b.to ?? '');
  return [...list].sort((a, b) => {
    if (sort === 'name') return byName(a, b);
    if (sort === 'runs') return b.runs - a.runs || byName(a, b);
    return RANK[a.status] - RANK[b.status] || b.runs - a.runs || byName(a, b);
  });
}

// ---------------------------------------------------------------- words

export const STATUS_WORD: Record<Integration['status'], string> = { ok: 'Running', failing: 'Failing', quiet: 'Overdue' };

/** "96 %" (no decimals unless it would round to 100 % with failures). */
export function percent(rate: number | null): string {
  if (rate === null) return '—';
  const p = rate * 100;
  if (p < 100 && p > 99.5) return `${p.toFixed(1)} %`;
  return `${Math.round(p)} %`;
}

// ---------------------------------------------------------------- paths and queries

export const systemPath = (code: string) => `/systems/${encodeURIComponent(code)}`;
export const integrationPath = (process: string) => `/integrations/${encodeURIComponent(process)}`;

/** The data search for an integration: its documents by the exact process name. */
export const integrationQuery = (process: string) => `@process="${process.replace(/"/g, '')}"`;

/**
 * The data search for a system: what the senders marked with its code or an alias (`@system=`), or what is in its data
 * models (the Cloud's detection: most data is not marked). One alternative per code and data model.
 */
export function systemQuery(code: string, aliases: readonly string[], classes: readonly string[]): string {
  return [...[code, ...aliases].map((c) => `@system=${c}`), ...classes.map((c) => `@class=${c}`)].join(' OR ');
}

// ---------------------------------------------------------------- what a search names

export interface NameableSystem { code: string; name: string; aliases: string[]; status?: string }
export type Named = { kind: 'system'; code: string } | { kind: 'integration'; process: string };

const unquote = (s: string) => s.replace(/^"(.*)"$/, '$1').trim();

/**
 * What a search exactly names: a system (its code, an alias or its name, or `@system=CODE`) or an integration (a known
 * process by its name, with or without its [PREFIX], or `@process="…"`). Anything more than that names nothing.
 */
export function namedThing(q: string, systems: readonly NameableSystem[], processes: readonly string[]): Named | null {
  const t = q.trim();
  if (!t) return null;
  const sys = /^@system[=:]("?)([^"\s]+)\1$/i.exec(t);
  const proc = /^@process[=:]"([^"]+)"$|^@process=(\S+)$/i.exec(t);
  const findSystem = (v: string) => {
    const low = v.toLowerCase();
    return systems.find((s) => s.status !== 'hidden' && (s.code.toLowerCase() === low || s.aliases.some((a) => a.toLowerCase() === low)))
      ?? systems.find((s) => s.status !== 'hidden' && s.name.toLowerCase() === low);
  };
  const findProcess = (v: string) => {
    const low = v.toLowerCase();
    return processes.find((p) => p.toLowerCase() === low) ?? processes.find((p) => shown(p) === low);
  };
  if (sys) {
    const s = findSystem(sys[2]!);
    return s ? { kind: 'system', code: s.code } : null;
  }
  if (proc) {
    const p = findProcess(proc[1] ?? proc[2]!);
    return p ? { kind: 'integration', process: p } : null;
  }
  if (t.startsWith('@') || /\s(OR|AND)\s/.test(t) || /[=<>()]/.test(t)) return null;
  const v = unquote(t);
  const s = findSystem(v);
  if (s) return { kind: 'system', code: s.code };
  const p = findProcess(v);
  return p ? { kind: 'integration', process: p } : null;
}

/** The integrations of one system: in (to it), out (from it), within; counted as the stage card says them. */
export function systemCounts(list: readonly Integration[], code: string): { integrations: number; failing: number; overdue: number } {
  const mine = list.filter((i) => i.from === code || i.to === code);
  return { integrations: mine.length, failing: mine.filter((i) => i.status === 'failing').length, overdue: mine.filter((i) => i.status === 'quiet').length };
}

// ---- how "Needs attention" is shown: a compact list (the default), the cards, or folded to one line. In the URL
// (`att=`, a history step: Back returns to the form before) and remembered in this browser as the next visit's default.

export type AttentionView = 'list' | 'cards' | 'hidden';
export const ATTENTION_VIEWS: readonly AttentionView[] = ['list', 'cards', 'hidden'];
const ATTENTION_PREF = 'mv.attention';

export function storedAttentionView(): AttentionView {
  try {
    const v = window.localStorage.getItem(ATTENTION_PREF);
    return ATTENTION_VIEWS.includes(v as AttentionView) ? (v as AttentionView) : 'list';
  } catch { return 'list'; }
}

export function readAttentionView(p: URLSearchParams, fallback: AttentionView = 'list'): AttentionView {
  const v = p.get('att');
  return ATTENTION_VIEWS.includes(v as AttentionView) ? (v as AttentionView) : fallback;
}

export function withAttentionView(p: URLSearchParams, v: AttentionView): URLSearchParams {
  const next = new URLSearchParams(p);
  next.set('att', v);
  try { window.localStorage.setItem(ATTENTION_PREF, v); } catch { /* a preference only */ }
  return next;
}

/** "2 failing · 1 overdue" — what the folded section says. */
export function attentionSummary(list: readonly Pick<Integration, 'status'>[]): string {
  const failing = list.filter((i) => i.status === 'failing').length;
  const overdue = list.length - failing;
  return [failing && `${failing} failing`, overdue && `${overdue} overdue`].filter(Boolean).join(' · ');
}
